/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect, useRef } from 'react';
import { Component, ComponentType, BidBodyCollectionItem } from '../../types';
import { apiClient } from '../../services/api';
import { Editor } from '@tinymce/tinymce-react';
import './BidBodyDetailsModal.css';

// Import TinyMCE
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.css';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

import tinymce from 'tinymce/tinymce';
import { registerMustachePlugin } from '../tinymce/mustacheTemplatePlugin';
registerMustachePlugin(tinymce);

interface BidBodyDetailsModalProps {
  component: Component;
  sequenceNumber: number;
  onClose: () => void;
  onSave: (details: BidBodyDetails) => void;
  initialData?: BidBodyDetails;
}

export interface CollectionItemDetails {
  componentId: number;
  actualCount: number;
}

export interface BidBodyDetails {
  componentId: number;
  sequenceNumber: number;
  notes?: string;
  identificationCode?: string;
  discountPercentage?: number;
  discountFlat?: number;
  count: number;
  collectionItems?: CollectionItemDetails[];
  frontPage?: string;
}

interface CollectionItemEdit {
  componentId: number;
  componentName: string;
  manufacturerCode: string;
  suggestedCount: number;
  actualCount: number;
}

const BidBodyDetailsModal: React.FC<BidBodyDetailsModalProps> = ({
  component,
  sequenceNumber,
  onClose,
  onSave,
  initialData
}) => {
  const isEditMode = !!initialData;
  const frontPageEditorRef = useRef<any>(null);

  const [formData, setFormData] = useState<BidBodyDetails>({
    componentId: component.id,
    sequenceNumber,
    notes: initialData?.notes || '',
    identificationCode: initialData?.identificationCode || '',
    count: initialData?.count || 1,
    collectionItems: initialData?.collectionItems || []
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [collectionItems, setCollectionItems] = useState<CollectionItemEdit[]>([]);
  const [isLoadingCollection, setIsLoadingCollection] = useState(false);
  // If editing with existing frontPage content, assume templates are filled (will be verified on editor init)
  const [hasUnfilledTemplates, setHasUnfilledTemplates] = useState(
    !!component.frontPageTemplate && !initialData?.frontPage
  );

  const checkUnfilledTemplates = () => {
    if (frontPageEditorRef.current) {
      const doc = frontPageEditorRef.current.getDoc();
      if (doc) {
        const remaining = doc.querySelectorAll('.mce-mustache-template');
        setHasUnfilledTemplates(remaining.length > 0);
      }
    }
  };

  // Fetch collection items if component is a COLLECTION
  useEffect(() => {
    const fetchCollectionItems = async () => {
      if (component.type === ComponentType.COLLECTION) {
        setIsLoadingCollection(true);
        try {
          const response = await apiClient.getCollectionItems(component.id);
          if (response.success && response.items) {
            const items: CollectionItemEdit[] = response.items.map((item: any) => {
              // If editing, use the existing actualCount from initialData
              const existingItem = initialData?.collectionItems?.find(
                ci => ci.componentId === item.component.id
              );
              return {
                componentId: item.component.id,
                componentName: item.component.name,
                manufacturerCode: item.component.manufacturerCode || '',
                suggestedCount: item.suggestedCount || 1,
                actualCount: existingItem?.actualCount || item.suggestedCount || 1
              };
            });
            setCollectionItems(items);
          }
        } catch (err) {
          console.error('Error fetching collection items:', err);
        } finally {
          setIsLoadingCollection(false);
        }
      }
    };

    fetchCollectionItems();
  }, [component]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // For numeric fields, parse to number
    if (name === 'count') {
      const numValue = value === '' ? 0 : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCollectionItemCountChange = (componentId: number, value: string) => {
    const numValue = value === '' ? 1 : Math.max(1, parseInt(value) || 1);
    setCollectionItems(prev => prev.map(item =>
      item.componentId === componentId
        ? { ...item, actualCount: numValue }
        : item
    ));
  };

  const validate = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (formData.count < 1) {
      newErrors.count = 'Count must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      // Clean up empty strings to undefined
      const cleanedData: BidBodyDetails = {
        ...formData,
        notes: formData.notes?.trim() || undefined,
        identificationCode: formData.identificationCode?.trim() || undefined
      };

      // Include collection items if this is a collection component
      if (component.type === ComponentType.COLLECTION && collectionItems.length > 0) {
        cleanedData.collectionItems = collectionItems.map(item => ({
          componentId: item.componentId,
          actualCount: item.actualCount
        }));
      }

      // Include frontPage content if the component has a front page template
      if (component.frontPageTemplate && frontPageEditorRef.current) {
        const frontPageContent = frontPageEditorRef.current.getContent();
        if (frontPageContent && frontPageContent.trim()) {
          cleanedData.frontPage = frontPageContent;
        }
      }

      onSave(cleanedData);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bid-body-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Component Details' : 'Add Component to Bid'}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="pdf-info">
            <h3>{component.name}</h3>
            <p>{component.manufacturerName} | {component.manufacturerCode}</p>
            {component.price && <p className="pdf-price">${component.price.toFixed(2)}</p>}
          </div>

          <form onSubmit={handleSubmit} className="bid-body-form">
            <div className="form-group">
              <label htmlFor="count">
                Count <span className="required">*</span>
              </label>
              <input
                type="number"
                id="count"
                name="count"
                value={formData.count}
                onChange={handleChange}
                min="1"
                required
              />
              {errors.count && <span className="error-text">{errors.count}</span>}
              <small className="help-text">Number of copies of this PDF (default: 1)</small>
            </div>

            <div className="form-group">
              <label htmlFor="identificationCode">Identification Code</label>
              <input
                type="text"
                id="identificationCode"
                name="identificationCode"
                value={formData.identificationCode}
                onChange={handleChange}
                placeholder="Enter identification code (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                placeholder="Enter any notes about this item (optional)"
              />
            </div>

            {/* Collection Items Section - only shown for COLLECTION type components */}
            {component.type === ComponentType.COLLECTION && (
              <div className="form-group">
                <label>Collection Items</label>
                {isLoadingCollection ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                    Loading collection items...
                  </div>
                ) : collectionItems.length > 0 ? (
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '0.75rem', color: '#374151' }}>Component</th>
                          <th style={{ textAlign: 'left', padding: '0.75rem', color: '#374151' }}>Manufacturer Code</th>
                          <th style={{ textAlign: 'center', padding: '0.75rem', color: '#374151', width: '100px' }}>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collectionItems.map((item) => (
                          <tr key={item.componentId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '0.75rem', color: '#1f2937' }}>{item.componentName}</td>
                            <td style={{ padding: '0.75rem', fontFamily: 'monospace', color: '#4b5563' }}>
                              {item.manufacturerCode || 'N/A'}
                            </td>
                            <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                              <input
                                type="number"
                                value={item.actualCount}
                                onChange={(e) => handleCollectionItemCountChange(item.componentId, e.target.value)}
                                min="1"
                                style={{
                                  width: '70px',
                                  padding: '0.25rem 0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  textAlign: 'center'
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                    No items in this collection
                  </div>
                )}
                <small className="help-text">
                  Adjust the count for each component in this collection
                </small>
              </div>
            )}

            {/* Front Page Editor - shown for components with a front page template */}
            {component.frontPageTemplate && (
              <div className="form-group">
                <label htmlFor="frontPage">Front Page</label>
                <Editor
                  onInit={(evt, editor) => {
                    frontPageEditorRef.current = editor;
                    // Move TinyMCE's auxiliary container (dialogs) to body to escape modal stacking context
                    const editorContainer = editor.getContainer();
                    if (editorContainer && editorContainer.parentNode) {
                      const parentNode = editorContainer.parentNode;
                      // Check if aux already exists
                      const existingAux = parentNode.querySelector('.tox-tinymce-aux');
                      if (existingAux instanceof HTMLElement) {
                        existingAux.style.zIndex = '10000';
                        document.body.appendChild(existingAux);
                      }
                      // Watch for aux being added later
                      const auxObserver = new MutationObserver((mutations) => {
                        for (const mutation of mutations) {
                          for (const node of Array.from(mutation.addedNodes)) {
                            if (node instanceof HTMLElement && node.classList.contains('tox-tinymce-aux')) {
                              node.style.zIndex = '10000';
                              document.body.appendChild(node);
                            }
                          }
                        }
                      });
                      auxObserver.observe(parentNode, { childList: true });
                    }
                    // Check after editor content is loaded
                    setTimeout(() => checkUnfilledTemplates(), 500);
                    // Observe DOM changes to detect when templates are filled
                    const doc = editor.getDoc();
                    if (doc && doc.body) {
                      const contentObserver = new MutationObserver(() => checkUnfilledTemplates());
                      contentObserver.observe(doc.body, { childList: true, subtree: true });
                    }
                  }}
                  licenseKey="gpl"
                  initialValue={initialData?.frontPage || component.frontPageTemplate || ''}
                  init={{
                    base_url: '/tinymce',
                    suffix: '.min',
                    height: 300,
                    menubar: false,
                    promotion: false,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'charmap', 'preview',
                      'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'table', 'help', 'wordcount', 'mustachetemplate'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'removeformat | help',
                    mustacheTemplateConfig: { collectionId: component.id, mode: 'fill' as const },
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                  }}
                />
                <small className="help-text">
                  Click on highlighted template fields to set their values
                </small>
              </div>
            )}

            <div className="modal-footer">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="submit-button" disabled={hasUnfilledTemplates}>
                {isEditMode ? 'Save Changes' : 'Add to Bid'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BidBodyDetailsModal;
